import { useState } from 'react';
import cn from 'classnames';

import { AppStatusEnum } from 'api-bible-shared-lib/utils/types';

import ConfirmDialog from './ConfirmDialog';
import { IconKey, IconPause, IconResume, IconCreditCard } from './Icons';
import { LoadingDots } from './Loading';
import Pill from './Pill';

import { useApplications } from '../contexts/Applications';
import { usePlans } from '../contexts/Plans';

export default function ApiKey({
  className,
  currentAppId,
}: {
  className?: string;
  currentAppId: string;
}) {
  const { plansById } = usePlans();
  const { applicationsById, defaultApplicationId, editApplication, redirectToCheckout } =
    useApplications();
  const app = applicationsById[currentAppId] || applicationsById[defaultApplicationId];

  const [error, setError] = useState<Error | undefined>();
  const [showConfirm, setShowConfirm] = useState(false);

  // handle loading state when user is being navigated to the stripe hosted payment page
  const [redirecting, setRedirecting] = useState(false);
  const [showCopied, setShowCopied] = useState(false);

  const handleMakePayment = () => {
    setRedirecting(true);

    // if this is a failed recurring billing
    if (app.stripeInvoiceUrl) {
      window.location.href = app.stripeInvoiceUrl;
      setRedirecting(false);
    }

    // if checkout was not completed
    redirectToCheckout(app.id).finally(() => setRedirecting(false));
  };

  function copyApiKey() {
    const input = document.getElementById('api-key') as HTMLInputElement | null;
    if (!input) return;
    input.select();
    input.setSelectionRange(0, 99999);
    document.execCommand('copy');
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2500);
  }

  async function updateApiKeyIsActiveStatus(isActiveKey: boolean) {
    setShowConfirm(false);
    try {
      await editApplication(app.id, { isActiveKey });
    } catch (e) {
      const err = e as Error;
      setError(err);
      setTimeout(() => setError(undefined), 5000);
    }
  }

  const authCacheNote = (
    <p className="italic mt-4">
      Please note that it might take up to 10 minutes for this to take effect.
    </p>
  );

  return (
    <div className={cn('text-white', className)}>
      <div className="inline-flex items-center">
        <div className="relative inline-flex items-center px-5 rounded-full border-solid border-2">
          <div className="rounded-full h-4 w-4" style={{ background: plansById[app.plan].color }} />
          <div className="px-2">{plansById[app.plan].name}</div>
          {app.status === AppStatusEnum.UNPAID && (
            <Pill className="text-black mx-2" color="orange">
              Unpaid
            </Pill>
          )}
          {app.status === AppStatusEnum.CANCELED && (
            <Pill className="text-black mx-2" color="red">
              Canceled
            </Pill>
          )}
          <div className="w-0.5 h-11 bg-white" />
          <div className="px-2 overflow-y-hidden ">
            <div className="inline-flex items-center">
              <button className="inline focus:outline-none" type="button" onClick={copyApiKey}>
                <IconKey
                  className={cn('inline w-5 h-5 m-2.5', {
                    'text-blue-500': !app.isActiveKey,
                  })}
                />
              </button>
              {/* input is hidden from view */}
              <input
                type="text"
                id="api-key"
                className="absolute opacity-0 top-full"
                value={app.apiKey}
                readOnly
              />
              <p
                className={cn(
                  'inline ml-.5 mr-2.5 font-thin mt-px text-xs sm:text-base font-mono',
                  {
                    'text-blue-500': !app.isActiveKey,
                  },
                )}
              >
                {app.apiKey}
              </p>
              {!app.isActiveKey && (
                <Pill className="text-black" color="gray">
                  Paused
                </Pill>
              )}
            </div>
          </div>
          {app.status !== AppStatusEnum.CANCELED && <div className="w-0.5 h-11 bg-white" />}
          {app.status === AppStatusEnum.ACTIVE && (
            <div className="inline-flex mx-2">
              <button
                className="inline-flex items-center focus:outline-none"
                type="button"
                onClick={() => setShowConfirm(true)}
              >
                {app.isActiveKey
                  ? [<IconPause key="iconPause" className="inline w-5 h-5" />, 'Pause']
                  : [<IconResume key="iconResume" className="inline w-5 h-5" />, 'Resume']}
              </button>
            </div>
          )}
          {app.status === AppStatusEnum.UNPAID &&
            (redirecting ? (
              <div className="inline-flex mx-2">
                <span className="mr-3" style={{ color: '#B5D2FF' }}>
                  Loading
                </span>
                <LoadingDots className="items-center w-7 h-7" />
              </div>
            ) : (
              <div className="inline-flex mx-2">
                <button
                  className="inline-flex items-center focus:outline-none"
                  type="button"
                  onClick={() => handleMakePayment()}
                >
                  <IconCreditCard key="iconMakePayment" className="inline w-5 h-5 mr-2" />
                  Make Payment
                </button>
              </div>
            ))}
          {showConfirm && (
            <ConfirmDialog
              body={
                <div>
                  {app.isActiveKey
                    ? [
                        'Are you sure you want to ',
                        <span key="confirm-pause" className="text-red-600">
                          pause
                        </span>,
                        ' this API key? Any calls made using it will be blocked temporarily until you activate it.',
                        authCacheNote,
                      ]
                    : [
                        'Are you sure you want to ',
                        <span key="confirm-resume" className="text-red-600">
                          resume
                        </span>,
                        ' this API key? Calls made using it will be allowed again.',
                        authCacheNote,
                      ]}
                </div>
              }
              handleCancel={() => setShowConfirm(false)}
              handleConfirm={() => updateApiKeyIsActiveStatus(!app.isActiveKey)}
            />
          )}
        </div>
        <p className="p-1 text-red-400">{error ? error.message : ''}</p>
        {showCopied && <p className="ml-2 uppercase text-md">Copied!</p>}
      </div>
    </div>
  );
}
